import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { IssueReport } from '../../api/types';
import { useDeleteIssueReportMutation } from '../../api/issueReport/issueReportArchiveApiSlice';
import { useIsMaster } from '../../common/useIsMaster';
import CustomButton from '../../components/CustomButton';
import { paths } from '../../routes';
import HardDeleteDialog from '../common/HardDeleteDialog';

/**
 * Master-only hard delete of a problem report — for test reports and erasure
 * requests; tenants archive instead. The server refuses a report a goodwill
 * code was issued for, and an open report that still holds a deposit; the
 * dialog lists the reason it named.
 */
const DeleteIssueReportButton = ({ issueReport }: { issueReport: IssueReport }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const isMaster = useIsMaster();
    const [open, setOpen] = useState(false);
    const [deleteIssueReport] = useDeleteIssueReportMutation();

    if (!isMaster) return null;

    // What the operator recognises the report by: what it concerns, and the
    // booking it was filed for, or the day it was filed when it has none.
    const subject = issueReport.door != null
        ? `${t('issue_report.locker')} ${issueReport.door}`
        : t('issue_report.general_issue_title');
    const name = `${subject} · ${issueReport.bookingNumber || moment(issueReport.createdAt).format('ll')}`;

    const listPath = location.pathname.startsWith('/rental')
        ? paths.rentalIssueReports
        : paths.issueReports;

    return (
        <>
            <CustomButton
                title={t('issue_reports.delete.action')}
                variant={'delete'}
                onClick={() => setOpen(true)}
                isFilled={false}
                fullWidth={false}
            />
            {open && (
                <HardDeleteDialog
                    name={name}
                    i18nPrefix={'issue_reports.delete'}
                    run={() => deleteIssueReport({ id: issueReport.id }).unwrap()}
                    onClose={() => setOpen(false)}
                    // The list refetches on mount, so the report is gone there.
                    onDeleted={() => navigate(listPath)}
                />
            )}
        </>
    );
};

export default DeleteIssueReportButton;
